import { Pagination, Spin } from 'antd';
import { useAtom, useAtomValue } from 'jotai';
import { searchPageNoAtom, searchPageSizeAtom } from 'packages/omnitag/state';
import {
  searchResultSizeRespAtom,
  searchQueryAtom,
} from 'packages/omnitag/state/text-search';
import { useHistory, useLocation } from 'react-router-dom';
import { useCallback, useEffect } from 'react';
import { changeURLParams } from 'packages/lib/utils/url';

export const ResultsPagination = (): JSX.Element | null => {
  const history = useHistory();
  const location = useLocation();
  const searchQuery = useAtomValue(searchQueryAtom);
  const resultSizeResp = useAtomValue(searchResultSizeRespAtom);
  const [pageSize, setPageSize] = useAtom(searchPageSizeAtom);
  const [pageNo, setPageNo] = useAtom(searchPageNoAtom);

  const onChange = useCallback(
    (pageNo: number, pageSize: number) => {
      setPageNo(pageNo);
      setPageSize(pageSize);

      changeURLParams(history, location, {
        pageSize: String(pageSize),
        pageNo: String(pageNo),
      });
    },
    [history, location, setPageNo, setPageSize]
  );

  // Corner case: ensure pageNo is within bounds. (User could go to last page then increase pageSize, resulting in pageNo being out of bounds)
  useEffect(() => {
    if (resultSizeResp && resultSizeResp.data) {
      if (pageNo * pageSize > resultSizeResp.data.count || pageNo < 1) {
        let newPageNo =
          Math.floor((resultSizeResp.data.count - 1) / pageSize) + 1;
        if (newPageNo < 1) newPageNo = 1;
        if (pageNo !== newPageNo) {
          onChange(newPageNo, pageSize);
        }
      }
    }
  }, [resultSizeResp, pageNo, pageSize, onChange, setPageNo]);

  const resultSizeRespIsValid =
    resultSizeResp && !resultSizeResp.error && !resultSizeResp.isLoading;
  const totalResults = resultSizeResp?.data?.count ?? 0;

  return (
    <>
      {resultSizeResp && resultSizeResp.isLoading && <Spin />}
      {resultSizeRespIsValid && (searchQuery?.text ?? '').length > 0 && (
        <Pagination
          onChange={onChange}
          total={totalResults}
          showTotal={(total, [n, m]): string => {
            if (total <= 0) {
              return 'No items.';
            }
            if (n <= 0 || m <= 0) {
              return `${total} items.`;
            }
            return `${n}-${m} of ${total} items`;
          }}
          showSizeChanger
          showQuickJumper
          pageSizeOptions={[20, 48, 100, 200]}
          current={pageNo}
          pageSize={pageSize}
        />
      )}
    </>
  );
};
